import React, { useCallback } from 'react';
import { Input, InputRef } from 'antd';
import { debounce } from 'lodash';

interface CurrencyInputProps {
  value?: string;
  onChange?: (value: number, rawText?: string) => void;
  onChangeDebounce?: (value: number) => void;
  bordered?: boolean;
  disabled?: boolean;
  size?: 'large' | 'medium' | 'small';
  className?: string;
  width?: React.CSSProperties['width'];
  height?: React.CSSProperties['height'];
  tabIndex?: number;
  inputRef?: React.Ref<InputRef>;
  nonSuffix?: boolean;
  maxValue?: number;
}

const PercentDecimalInputPrecision: React.FC<CurrencyInputProps> = ({
  value = '0.0',
  onChange,
  onChangeDebounce,
  bordered = true,
  size = 'medium',
  className = '',
  width,
  height,
  disabled,
  tabIndex,
  inputRef,
  nonSuffix,
  maxValue,
}) => {
  const _debounce = useCallback(
    debounce(
      (inputValue: number) =>
        typeof onChangeDebounce === 'function' && onChangeDebounce(inputValue),
      200
    ),
    []
  );

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value.replace(/[^0-9]/g, ''); // Strip non-numeric characters
    let numericValue = 0.0;

    if (rawValue) {
      const intValue = parseInt(rawValue);
      numericValue = intValue / 10;
    }
    if (maxValue && numericValue > maxValue) {
      return;
    }

    if (onChange) {
      onChange(numericValue, numericValue.toFixed(1));
    }
    _debounce(numericValue);
  };

  const displayValue = `${parseFloat(value || '0.0').toFixed(1)}`;

  // Determine the height based on the size prop
  let heightBySize = '3.5rem'; // default for 'medium'
  switch (size) {
    case 'large':
      heightBySize = '4rem';
      break;
    case 'small':
      heightBySize = '3rem';
      break;
  }

  return (
    <Input
      ref={inputRef}
      className={`percent-decimal-input-precision ${className}`}
      value={displayValue}
      onChange={handleInputChange}
      style={{ width: width || '100%', height: height || heightBySize }}
      bordered={bordered}
      disabled={disabled}
      tabIndex={tabIndex}
      suffix={!nonSuffix ? '%' : undefined}
    />
  );
};

export default PercentDecimalInputPrecision;
